import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Users, Mail, Target, Heart, Zap, FileText, ArrowLeft, 
  Shield, CheckCircle, Sparkles
} from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold text-primary">FixResume24</span>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4">
            <Users className="h-3 w-3 mr-1" />
            About Us
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Our Mission
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Empowering professionals to land their dream jobs with perfectly crafted resumes.
          </p>
        </div>

        {/* Values Section */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <Card className="border-slate-200 text-center hover:shadow-lg transition-shadow">
            <CardContent className="p-8">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Target className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-3">Our Vision</h3>
              <p className="text-muted-foreground">
                To become the most trusted resume transformation platform, helping millions of professionals present their best selves to employers.
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 text-center hover:shadow-lg transition-shadow">
            <CardContent className="p-8">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Heart className="h-7 w-7 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-3">Why We Built This</h3>
              <p className="text-muted-foreground">
                We noticed talented professionals getting overlooked due to poorly formatted resumes. FixResume24 bridges that gap with smart, ATS-optimized templates.
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 text-center hover:shadow-lg transition-shadow">
            <CardContent className="p-8">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <Zap className="h-7 w-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold mb-3">What Sets Us Apart</h3>
              <p className="text-muted-foreground">
                AI-powered ATS optimization, instant preview, 29+ professional templates, and a commitment to your privacy. Your resume, your success.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* About Company Section */}
        <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200 shadow-sm mb-16">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg">
                <FileText className="h-12 w-12 text-white" />
              </div>
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-2xl font-bold text-slate-900 mb-3">About FixResume24</h2>
              <p className="text-muted-foreground mb-4">
                Built with passion for job seekers worldwide. We believe everyone deserves a resume that truly represents their skills and potential. 
                Our platform combines cutting-edge AI technology with professional design principles to help you stand out in today's competitive job market.
              </p>
              <p className="text-muted-foreground">
                Whether you're a fresh graduate, an experienced professional looking for a change, or someone re-entering the workforce, 
                FixResume24 is here to help you make a lasting first impression.
              </p>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">What We Offer</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-50">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium">29+ Professional Templates</h4>
                <p className="text-sm text-muted-foreground">Designed for various industries and career levels</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-50">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium">ATS-Optimized Formats</h4>
                <p className="text-sm text-muted-foreground">Pass through Applicant Tracking Systems with ease</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-50">
              <Sparkles className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium">AI-Powered Refinement</h4>
                <p className="text-sm text-muted-foreground">Enhance your content with smart suggestions</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-50">
              <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium">Privacy First</h4>
                <p className="text-sm text-muted-foreground">Your data is secure and never shared</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="text-center">
          <Badge variant="outline" className="mb-4">
            <Mail className="h-3 w-3 mr-1" />
            Contact Us
          </Badge>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Get In Touch</h2>
          <p className="text-muted-foreground mb-8">
            Have questions or feedback? We'd love to hear from you!
          </p>

          <Card className="border-slate-200 max-w-md mx-auto">
            <CardContent className="p-8">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Email Us</h3>
              <p className="text-muted-foreground mb-4">
                We'd love to hear from you!
              </p>
              <a 
                href="mailto:support@fixresume24.com" 
                className="inline-flex items-center gap-2 text-lg font-medium text-primary hover:underline"
              >
                <Mail className="h-5 w-5" />
                support@fixresume24.com
              </a>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-100 bg-slate-50 mt-16">
        <div className="max-w-7xl mx-auto px-4 flex flex-col items-center gap-4">
          <div className="flex items-center gap-6">
            <Link to="/" className="text-sm text-slate-600 hover:text-primary transition-colors">
              Home
            </Link>
            <Link to="/terms" className="text-sm text-slate-600 hover:text-primary transition-colors">
              Terms & Conditions
            </Link>
            <Link to="/privacy-policy" className="text-sm text-slate-600 hover:text-primary transition-colors">
              Privacy Policy
            </Link>
          </div>
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} FixResume24. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
